import { Icon } from '@veeva/corgix/icon';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
    ACTIVE_SITES,
    APP_LICENSE_POOL,
    APPLICATION_BASE,
    APPLICATION_BASE_NAME,
    APPLICATION_NAME,
    APPLICATIONS,
    ARCHIVE_SITES,
    DETAILS,
    DOCUMENT_BUNDLES,
    FAMILY_METADATA,
    INVESTIGATOR_PORTAL_SITES,
    LABEL,
    LABELS,
    LICENSE_COUNTS,
    LICENSE_INFORMATION,
    LICENSED,
    LICENSING_DOMAIN,
    LICENSING_MODEL,
    MANAGED_SERVICE_USERS,
    MEMBER_USERTYPES,
    METADATA,
    MODEL,
    MULTI_APP_LICENSE_POOL,
    NAME,
    NOTES,
    PER_USER,
    PROPERTIES,
    SHARED,
    SITE_BASED,
    SITE_LICENSING,
    START_DATE,
    STATION_DEVICE_LICENSES,
    TERM,
    TYPE,
    USAGE_LICENSING,
    USED,
    USER_BASED,
    USER_LICENSING,
    VALUES,
} from './LicensingConstants';
import {
    licenseContainerCenterLabelCSS,
    licenseDialogContentCSS,
    licenseInformationContainerCSS,
    licenseLoadingSpinnerCSS,
    licenseMiscInfoColumnCSS,
    licenseMiscInfoSectionCSS,
    licenseMiscInfoRowCSS,
    licenseMiscInfoRowLabelCSS,
    licenseMiscInfoRowTitleCSS,
    licenseRowAppColumnContainerCSS,
    licenseRowAppLabelColumnCSS,
    licenseRowColumnCSS,
    licenseRowContainerCSS,
    licenseRowTitleCSS,
    licenseRowValueCSS,
    licenseSectionCSS,
    toggleNoteLengthCSS,
    licenseMiscInfoContainerCSS,
    licensePoolHelpTextDialogCSS,
} from './styles/LicenseInformation.styles';
import useGetLicenseInformation from '../../services/data/useGetLicenseInformation';
import InstancePageContext from '../InstancePageContext';
import LicenseInformationMobileDialog from './LicenseInformationMobileDialog';

export default function LicenseInformationMobile() {
    const { instanceId } = useParams();
    const { licenseData, setLicenseData, useLongNote, setUseLongNote } =
        useContext(InstancePageContext);
    const [data, isLoading, error] = useGetLicenseInformation(instanceId, licenseData);

    const toggleUseLongNote = () => {
        setUseLongNote(!useLongNote);
    };

    function getLicenseInformation() {
        if (error) {
            return null;
        }
        if (licenseData) {
            return licenseData;
        }
        setLicenseData(data);
        return data;
    }

    function getMiscLicenseInfo() {
        const responseLicenseInfo = getLicenseInformation();
        const licenseInfo = responseLicenseInfo[LICENSE_INFORMATION];
        const familyMetadata = responseLicenseInfo[FAMILY_METADATA];
        const baseApp = licenseInfo[APPLICATION_BASE];

        const baseAppMetadata = familyMetadata[APPLICATIONS]?.find(
            (app) => app[APPLICATION_NAME] === baseApp[APPLICATION_BASE_NAME],
        );
        const licenseModel = licenseInfo[LICENSING_MODEL];
        const miscItems = [];

        const getDataIfPresent = (map, key, title) => {
            if (map) {
                if (map[key] != null) {
                    // For the notes label, add a See more button if text is too long
                    if (key === NOTES && map[NOTES].length > 120) {
                        const truncatedNote = `${map[NOTES].slice(0, 120)}... `;
                        const seeMoreComponent = (
                            <div>
                                <span>{useLongNote ? map[NOTES] : truncatedNote}</span>
                                {/* using span as toggle for simplicity */}
                                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                                <span onClick={toggleUseLongNote} css={toggleNoteLengthCSS}>
                                    {useLongNote ? 'See less' : 'See more'}
                                </span>
                            </div>
                        );
                        miscItems.push([title, seeMoreComponent]);
                    } else {
                        miscItems.push([title, map[key]]);
                    }
                }
            }
        };

        const getSiteBasedDataIfPresent = () => {
            const siteBaseApp = responseLicenseInfo[LICENSE_COUNTS][APPLICATIONS]?.find(
                (app) => app[APPLICATION_NAME] === baseApp[APPLICATION_BASE_NAME],
            );
            const siteLicensing = siteBaseApp[SITE_LICENSING];
            miscItems.push([
                'Active Sites',
                `${siteLicensing[ACTIVE_SITES][USED]} out of ${siteLicensing[ACTIVE_SITES][LICENSED]}`,
            ]);
            miscItems.push([
                'Archived Sites',
                `${siteLicensing[ARCHIVE_SITES][USED]} out of ${siteLicensing[ARCHIVE_SITES][LICENSED]}`,
            ]);
            miscItems.push([
                'Investigator Portal Sites',
                `${siteLicensing[INVESTIGATOR_PORTAL_SITES][USED]} out of ${siteLicensing[INVESTIGATOR_PORTAL_SITES][LICENSED]}`,
            ]);
        };

        getDataIfPresent(familyMetadata, NAME, 'Family');
        getDataIfPresent(licenseInfo, LICENSING_DOMAIN, 'Licensing Domain');
        getDataIfPresent(baseAppMetadata, MODEL, 'Licensing Model');
        if (licenseModel === SITE_BASED) {
            getSiteBasedDataIfPresent();
        }
        getDataIfPresent(baseApp, MANAGED_SERVICE_USERS, 'Managed Services');
        getDataIfPresent(baseApp, DOCUMENT_BUNDLES, 'Document Bundles');
        getDataIfPresent(baseApp, STATION_DEVICE_LICENSES, 'Station Device Licenses');
        getDataIfPresent(licenseInfo, NOTES, 'Notes');

        const columnA = miscItems.slice(0, Math.ceil(miscItems.length / 2));
        const columnB = miscItems.slice(Math.ceil(miscItems.length / 2));

        return (
            <div css={licenseMiscInfoContainerCSS}>
                <div css={licenseMiscInfoSectionCSS}>
                    <div css={licenseMiscInfoColumnCSS}>
                        {columnA.map((keyPair) => (
                            <div key={keyPair[0]} css={licenseMiscInfoRowCSS}>
                                <span css={licenseMiscInfoRowTitleCSS}>{keyPair[0]}</span>
                                <span css={licenseMiscInfoRowLabelCSS}>{keyPair[1]}</span>
                            </div>
                        ))}
                    </div>
                    <div css={licenseMiscInfoColumnCSS}>
                        {columnB.map((keyPair) => (
                            <div key={keyPair[0]} css={licenseMiscInfoRowCSS}>
                                <span css={licenseMiscInfoRowTitleCSS}>{keyPair[0]}</span>
                                <span css={licenseMiscInfoRowLabelCSS}>{keyPair[1]}</span>
                            </div>
                        ))}
                    </div>
                </div>
                {licenseModel === USER_BASED && (
                    <div css={licensePoolHelpTextDialogCSS}>
                        <span>
                            *Multi-App License Pool - Used when license counts for user types are
                            pooled across Applications
                            <br />
                            *App License Pool - Used when license counts for user types are pooled
                            for a Single Application
                        </span>
                    </div>
                )}
            </div>
        );
    }

    function getAppKeyToUserBasedCounts() {
        const instanceLicenseCounts = getLicenseInformation()[LICENSE_COUNTS];
        const userKeyToCountsMap = {};

        instanceLicenseCounts[APPLICATIONS].forEach((app) => {
            const appKey = app[APPLICATION_NAME];
            const userLicensing = app[USER_LICENSING];
            const userLicensingMap = {};

            if (userLicensing != null) {
                Object.entries(userLicensing).forEach(([key, countMap]) => {
                    const userCountMap = {};
                    let licensedLabel = countMap[LICENSED];
                    if (countMap[SHARED]) {
                        licensedLabel = '--';
                    } else if (licensedLabel === 'unlimited') {
                        licensedLabel = 'Unlimited';
                    }
                    userCountMap[LICENSED] = licensedLabel;
                    userCountMap[USED] = countMap[USED];
                    userLicensingMap[key] = userCountMap;
                });
                userKeyToCountsMap[appKey] = userLicensingMap;
            }
        });

        return userKeyToCountsMap;
    }

    function getAppKeyToUsageBasedCounts() {
        const instanceLicenseCounts = getLicenseInformation()[LICENSE_COUNTS];
        const usageKeyToCountsMap = {};

        // .entries() returns an Iterator, must use for syntax here...
        // eslint-disable-next-line no-restricted-syntax
        for (const app of instanceLicenseCounts[APPLICATIONS]) {
            const appKey = app[APPLICATION_NAME];
            const usageLicensing = app[USAGE_LICENSING];
            const usageLicensingMap = {};

            if (usageLicensing != null) {
                usageLicensing[PROPERTIES].forEach((license) => {
                    Object.entries(license).forEach(([key, countMap]) => {
                        const usageCountMap = {};
                        usageCountMap[LICENSED] =
                            countMap[LICENSED] === 'unlimited' ? 'Unlimited' : countMap[LICENSED];
                        usageCountMap[USED] = countMap[USED];
                        usageLicensingMap[key] = usageCountMap;
                    });
                });
                usageKeyToCountsMap[appKey] = usageLicensingMap;
            }
        }

        return usageKeyToCountsMap;
    }

    function renderUserTypes(userTypeCountsForApp, metadata) {
        const memberUserTypes = metadata[PROPERTIES].find((md) => md.name === MEMBER_USERTYPES);
        const memberUserTypesMap = {};

        // .entries() returns an Iterator, must use for syntax here...
        // eslint-disable-next-line no-restricted-syntax
        for (const [i, key] of memberUserTypes[VALUES].entries()) {
            memberUserTypesMap[key] = memberUserTypes[LABELS][i];
        }

        return memberUserTypes[VALUES].map((key) => {
            const typeCounts = userTypeCountsForApp[key];
            const countLabel =
                typeCounts != null ? `${typeCounts[USED]}/${typeCounts[LICENSED]}` : 'N/A';
            const memberUserTypeLabel =
                memberUserTypesMap[key] != null ? memberUserTypesMap[key] : key;
            return (
                <div key={memberUserTypeLabel} css={licenseRowColumnCSS}>
                    <span css={licenseRowTitleCSS}>{memberUserTypeLabel}</span>
                    <span css={licenseRowValueCSS}>{countLabel}</span>
                </div>
            );
        });
    }

    function renderUsageTypes(usageKeyToLabel, appUsageCounts) {
        return Object.entries(usageKeyToLabel).map(([key, label]) => {
            const typeCounts = appUsageCounts[key];

            const countLabel =
                typeCounts != null ? `${typeCounts[USED]}/${typeCounts[LICENSED]}` : 'N/A';
            return (
                <div key={label} css={licenseRowColumnCSS}>
                    <span css={licenseRowTitleCSS}>{label}</span>
                    <span css={licenseRowValueCSS}>{countLabel}</span>
                </div>
            );
        });
    }

    function renderUserBasedLicensing() {
        const responseLicenseInfo = getLicenseInformation();
        const licenseInfo = responseLicenseInfo[LICENSE_INFORMATION];
        const baseApp = licenseInfo[APPLICATION_BASE];

        const applications = licenseInfo[APPLICATIONS]?.filter((app) => {
            const alp = app[TYPE] === APP_LICENSE_POOL;
            const mal = app[TYPE] === MULTI_APP_LICENSE_POOL;
            return mal || alp || (app[DETAILS] != null && app[DETAILS][USER_LICENSING] != null);
        });

        if (!applications || applications.length === 0) {
            return null;
        }

        const metadata = responseLicenseInfo[METADATA];
        const appLicenseTypeMap = {};
        const appLicenseTypes = metadata[PROPERTIES].find((md) => md.name === TYPE);

        // .entries() returns an Iterator, must use for syntax here...
        // eslint-disable-next-line no-restricted-syntax
        for (const [i, key] of appLicenseTypes[VALUES].entries()) {
            appLicenseTypeMap[key] = appLicenseTypes[LABELS][i];
        }

        const familyMetadata = responseLicenseInfo[FAMILY_METADATA];
        const familyKeyToLabelMap = {};
        familyMetadata[APPLICATIONS].forEach((appMetadata) => {
            familyKeyToLabelMap[appMetadata[APPLICATION_NAME]] = appMetadata[LABEL];
        });

        const userKeyToCountsMap = getAppKeyToUserBasedCounts();
        return (
            <div css={licenseSectionCSS}>
                <span>User Licensing</span>
                <div css={licenseInformationContainerCSS}>
                    {applications.map((app) => {
                        const appUsageCounts = userKeyToCountsMap[app[APPLICATION_NAME]];
                        const appLabel =
                            familyKeyToLabelMap[app[APPLICATION_NAME]] != null
                                ? familyKeyToLabelMap[app[APPLICATION_NAME]]
                                : app[APPLICATION_NAME];
                        let typeLabel;
                        if (app[TYPE] === APP_LICENSE_POOL) {
                            typeLabel = `App License Pool (${app[DETAILS][LICENSED]})`;
                        } else if (app[TYPE] === MULTI_APP_LICENSE_POOL) {
                            typeLabel = `Multi-App License Pool (${baseApp[DETAILS][LICENSED]})`;
                        } else if (app[TYPE] === PER_USER) {
                            typeLabel = 'Per User';
                        }

                        return (
                            <div key={appLabel} css={licenseRowContainerCSS}>
                                <div css={licenseRowAppLabelColumnCSS}>
                                    <span>{appLabel}</span>
                                </div>
                                <div css={licenseRowAppColumnContainerCSS}>
                                    <div css={licenseRowColumnCSS}>
                                        <span css={licenseRowTitleCSS}>Type</span>
                                        <span css={licenseRowValueCSS}>{typeLabel}</span>
                                    </div>
                                    {renderUserTypes(appUsageCounts, metadata)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    function renderUsageBasedLicensing() {
        const responseLicenseInfo = getLicenseInformation();
        const licenseInfo = responseLicenseInfo[LICENSE_INFORMATION];
        const baseApp = licenseInfo[APPLICATION_BASE];
        const usageApps = [];

        if (baseApp[DETAILS] && baseApp[DETAILS][USAGE_LICENSING] != null) {
            usageApps.push(baseApp);
        }
        const usageSubApps = licenseInfo[APPLICATIONS]?.filter(
            (app) => app[DETAILS] != null && app[DETAILS][USAGE_LICENSING] != null,
        );
        if (usageSubApps != null) {
            usageApps.push(...usageSubApps);
        }

        if (usageApps.length === 0) {
            return null;
        }

        const familyMetadata = responseLicenseInfo[FAMILY_METADATA];
        const familyKeyToLabelMap = {};
        const usageKeyToLabel = {};
        familyMetadata[APPLICATIONS].forEach((appMetadata) => {
            familyKeyToLabelMap[appMetadata[APPLICATION_NAME]] = appMetadata[LABEL];
            if (appMetadata[USAGE_LICENSING] != null) {
                appMetadata[USAGE_LICENSING].forEach((lic) => {
                    usageKeyToLabel[lic[NAME]] = lic[LABEL];
                });
            }
        });

        const usageKeyToCountsMap = getAppKeyToUsageBasedCounts();
        return (
            <div css={licenseSectionCSS}>
                <span>Usage Licensing</span>
                <div css={licenseInformationContainerCSS}>
                    {usageApps.map((app) => {
                        // Have to grab base app key differently
                        const appKey =
                            app[APPLICATION_BASE_NAME] != null
                                ? app[APPLICATION_BASE_NAME]
                                : app[APPLICATION_NAME];
                        const appLabel =
                            familyKeyToLabelMap[appKey] != null
                                ? familyKeyToLabelMap[appKey]
                                : appKey;
                        const usageLicense = app[DETAILS][USAGE_LICENSING];
                        const appUsageCounts = usageKeyToCountsMap[appKey];

                        return (
                            <div key={appLabel} css={licenseRowContainerCSS}>
                                <div css={licenseRowAppLabelColumnCSS}>
                                    <span>{appLabel}</span>
                                </div>
                                <div css={licenseRowAppColumnContainerCSS}>
                                    <div css={licenseRowColumnCSS}>
                                        <span css={licenseRowTitleCSS}>Start Date</span>
                                        <span css={licenseRowValueCSS}>
                                            {usageLicense[START_DATE]}
                                        </span>
                                    </div>
                                    <div css={licenseRowColumnCSS}>
                                        <span css={licenseRowTitleCSS}>Term</span>
                                        <span css={licenseRowValueCSS}>{usageLicense[TERM]}</span>
                                    </div>
                                    {renderUsageTypes(usageKeyToLabel, appUsageCounts)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <LicenseInformationMobileDialog>
                <div css={licenseContainerCenterLabelCSS}>
                    <span>Retrieving Information</span>
                </div>
                <div css={licenseLoadingSpinnerCSS}>
                    <Icon type={faSpinner} spin size="lg" title="Loading..." />
                </div>
            </LicenseInformationMobileDialog>
        );
    }

    return error ? (
        <LicenseInformationMobileDialog>
            <div css={licenseContainerCenterLabelCSS}>
                <span>License Information not available.</span>
            </div>
        </LicenseInformationMobileDialog>
    ) : (
        <LicenseInformationMobileDialog>
            <div css={licenseDialogContentCSS}>
                {getMiscLicenseInfo()}
                {renderUserBasedLicensing()}
                {renderUsageBasedLicensing()}
            </div>
        </LicenseInformationMobileDialog>
    );
}

LicenseInformationMobile.propTypes = {
    licenseData: PropTypes.shape({
        familyMetadata: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
        licenseCounts: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
        licenseInformation: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
        metadata: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
    }),
};
